import React, { useState } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Space,
  Alert,
  message,
  Tag,
  Divider,
  Table,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { controlloQuantitaAPI } from '../services/api.ts';

const { Title, Text, Paragraph } = Typography;

interface FileValidation {
  filename: string;
  valid: boolean;
  errors: string[];
}

interface ValidateResult {
  valid: boolean;
  files: {
    A: FileValidation;
    B: FileValidation;
  };
}

interface SheetStat {
  sheet: string;
  codes: number;
  orange: number;
  red: number;
  ok: number;
  invalid_qty: number;
}

interface Anomaly {
  sheet: string;
  row: number;
  code: string;
  value: string;
  reason: string;
}

interface BInfo {
  sheet: string;
  codes: number;
  duplicate_codes: number;
  invalid_count: number;
  invalid: { row: number; code: string; value: string }[];
}

interface ProcessStats {
  sheets_processed: number;
  codes_total: number;
  orange: number;
  red: number;
  ok: number;
  invalid_qty: number;
  per_sheet: SheetStat[];
  anomalies: Anomaly[];
  b: BInfo;
}

type Slot = 'A' | 'B';

const SLOT_LABELS: Record<Slot, string> = {
  A: 'A - File Aziendale (tutti i fogli)',
  B: 'B - File Gestionale Oron (foglio Export Oron - 3)',
};

const ACCEPT = '.xls,.xlsx,.ods';

// Il detail del backend può essere una stringa (HTTPException) o una lista di
// errori di validazione FastAPI (422): in entrambi i casi un testo leggibile.
const errorText = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
  }
  if (error?.code === 'ECONNABORTED') return 'Tempo di attesa scaduto: il file è troppo grande o il server è occupato.';
  return fallback;
};

const ControlloQuantitaOron: React.FC = () => {
  const [files, setFiles] = useState<Record<Slot, File | null>>({ A: null, B: null });
  const [fileLists, setFileLists] = useState<Record<Slot, UploadFile[]>>({ A: [], B: [] });
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validation, setValidation] = useState<ValidateResult | null>(null);
  const [result, setResult] = useState<ProcessStats | null>(null);

  const allFilesSelected = !!files.A && !!files.B;
  const canProcess = !!validation?.valid;

  const resetValidation = () => {
    setValidation(null);
    setResult(null);
  };

  const handleSelect = (slot: Slot, file: File) => {
    setFiles((prev) => ({ ...prev, [slot]: file }));
    setFileLists((prev) => ({
      ...prev,
      [slot]: [{ uid: slot, name: file.name, status: 'done' } as UploadFile],
    }));
    resetValidation();
  };

  const handleRemove = (slot: Slot) => {
    setFiles((prev) => ({ ...prev, [slot]: null }));
    setFileLists((prev) => ({ ...prev, [slot]: [] }));
    resetValidation();
  };

  const handleValidate = async () => {
    if (!files.A || !files.B) return;
    setValidating(true);
    resetValidation();
    try {
      const data: ValidateResult = await controlloQuantitaAPI.validate(files.A, files.B);
      setValidation(data);
      if (data.valid) {
        message.success('Validazione superata: puoi avviare il controllo dei dati.');
      } else {
        message.warning('Validazione non superata: correggi i file segnalati.');
      }
    } catch (error: any) {
      message.error(errorText(error, 'Errore durante la validazione dei file'));
    } finally {
      setValidating(false);
    }
  };

  const handleProcess = async () => {
    if (!files.A || !files.B) return;
    setProcessing(true);
    setResult(null);
    try {
      const { stats, filenames } = await controlloQuantitaAPI.process(files.A, files.B);
      message.success(`Controllo completato. File scaricato: ${filenames.join(', ')}`);
      setResult(stats as ProcessStats);
    } catch (error: any) {
      message.error(errorText(error, 'Errore durante il controllo dei dati'));
    } finally {
      setProcessing(false);
    }
  };

  const renderUpload = (slot: Slot) => (
    <div>
      <Text strong>{SLOT_LABELS[slot]}</Text>
      <div style={{ marginTop: 4 }}>
        <Upload
          accept={ACCEPT}
          maxCount={1}
          fileList={fileLists[slot]}
          beforeUpload={(f) => {
            handleSelect(slot, f as unknown as File);
            return false;
          }}
          onRemove={() => handleRemove(slot)}
        >
          <Button icon={<UploadOutlined />}>Seleziona file (.xls / .xlsx / .ods)</Button>
        </Upload>
      </div>
    </div>
  );

  const renderFileResult = (slot: Slot, info?: FileValidation) => {
    if (!info) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <Space>
          {info.valid ? <Tag color="green">{slot} OK</Tag> : <Tag color="red">{slot} ERRORE</Tag>}
          <Text type="secondary">{info.filename}</Text>
        </Space>
        {info.errors.length > 0 && (
          <ul style={{ margin: '4px 0 0 0', color: '#cf1322' }}>
            {info.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderResult = (r: ProcessStats) => (
    <Card style={{ marginTop: 16 }} title="Esito del controllo">
      <Row gutter={16}>
        <Col xs={12} md={4}><Statistic title="Fogli" value={r.sheets_processed} /></Col>
        <Col xs={12} md={4}><Statistic title="Codici" value={r.codes_total} /></Col>
        <Col xs={12} md={4}><Statistic title="Coincidenti" value={r.ok} valueStyle={{ color: '#389e0d' }} /></Col>
        <Col xs={12} md={4}><Statistic title="Quantità diverse" value={r.orange} valueStyle={{ color: '#d46b08' }} /></Col>
        <Col xs={12} md={4}><Statistic title="Codici non in B" value={r.red} valueStyle={{ color: '#cf1322' }} /></Col>
        <Col xs={12} md={4}><Statistic title="Q.TA' non leggibili" value={r.invalid_qty} valueStyle={{ color: r.invalid_qty ? '#d4b106' : undefined }} /></Col>
      </Row>

      <Divider orientation="left">Dettaglio per foglio</Divider>
      <Table<SheetStat>
        size="small"
        rowKey="sheet"
        pagination={false}
        dataSource={r.per_sheet}
        columns={[
          { title: 'Foglio', dataIndex: 'sheet' },
          { title: 'Codici', dataIndex: 'codes', align: 'right' },
          { title: 'Coincidenti', dataIndex: 'ok', align: 'right' },
          { title: 'Quantità diverse', dataIndex: 'orange', align: 'right', render: (v: number) => (v ? <Tag color="orange">{v}</Tag> : 0) },
          { title: 'Codici non in B', dataIndex: 'red', align: 'right', render: (v: number) => (v ? <Tag color="red">{v}</Tag> : 0) },
          { title: "Q.TA' non leggibili", dataIndex: 'invalid_qty', align: 'right', render: (v: number) => (v ? <Tag color="gold">{v}</Tag> : 0) },
        ]}
      />

      {r.b && (
        <>
          <Divider orientation="left">File B</Divider>
          <Space direction="vertical">
            <Text>
              Foglio letto: <Text code>{r.b.sheet}</Text> — codici distinti: {r.b.codes}
            </Text>
            {r.b.duplicate_codes > 0 && (
              <Alert
                type="info"
                showIcon
                message={`${r.b.duplicate_codes} codici compaiono su più righe di B: le loro giacenze sono state sommate.`}
              />
            )}
            {r.b.invalid_count > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${r.b.invalid_count} giacenze di B non sono numeri e sono state contate come 0.`}
                description={
                  <ul style={{ margin: 0 }}>
                    {r.b.invalid.map((x, i) => (
                      <li key={i}>Riga {x.row}: {x.code.split('|').join(' ')} — valore "{x.value}"</li>
                    ))}
                  </ul>
                }
              />
            )}
          </Space>
        </>
      )}

      {r.anomalies.length > 0 && (
        <>
          <Divider orientation="left">Q.TA' non leggibili nel file A (celle gialle)</Divider>
          <Table<Anomaly>
            size="small"
            rowKey={(a) => `${a.sheet}-${a.row}`}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            dataSource={r.anomalies}
            columns={[
              { title: 'Foglio', dataIndex: 'sheet' },
              { title: 'Riga', dataIndex: 'row', align: 'right' },
              { title: 'Codice', dataIndex: 'code', render: (v: string) => v.split('|').join(' ') },
              { title: 'Valore', dataIndex: 'value' },
              { title: 'Motivo', dataIndex: 'reason' },
            ]}
          />
          {r.invalid_qty > r.anomalies.length && (
            <Text type="secondary">Mostrate le prime {r.anomalies.length} su {r.invalid_qty}: le altre sono gialle nel file scaricato.</Text>
          )}
        </>
      )}
    </Card>
  );

  return (
    <div>
      <Title level={3}>
        <FileExcelOutlined /> Controllo Quantità con Oron
      </Title>
      <Paragraph type="secondary">
        Confronta le quantità tra <strong>A - File Aziendale</strong> (<strong>tutti i fogli</strong>) e
        <strong> B - File Gestionale Oron</strong> (foglio <Text code>Export Oron - 3</Text>).
        Il controllo elabora <strong>ogni foglio di A separatamente</strong>: per ogni foglio si somma la
        <Text code>Q.TA'</Text> per codice composto (A: <Text code>CODICE+MISURA+COD COL.</Text>, B:
        <Text code>Oggetto+TG+COL</Text>) e si confronta con la <Text code>Giacenza</Text> di B (se un codice
        compare su più righe di B le giacenze vengono sommate). Vengono considerate solo le righe con
        <Text code>CODICE</Text> valorizzato; l'intestazione può ripetersi prima di ogni gruppo e le righe-titolo
        che occupano più colonne vengono ignorate. Le formule sono lette con il loro risultato. Se un foglio non
        contiene le colonne attese il controllo si interrompe con errore.
        Dopo la validazione, il controllo produce <strong>una copia di A</strong> (con timestamp, tutti i
        fogli) in cui la cella <Text code>Q.TA'</Text> è evidenziata:
        <Tag color="orange" style={{ marginLeft: 6 }}>arancione</Tag> = somma diversa da B,
        <Tag color="red">rosso</Tag> = codice non presente in B,
        <Tag color="gold">giallo</Tag> = quantità non leggibile (esclusa dalla somma).
      </Paragraph>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {renderUpload('A')}
          {renderUpload('B')}

          <Space>
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={handleValidate}
              loading={validating}
              disabled={!allFilesSelected || processing}
            >
              Valida file
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleProcess}
              loading={processing}
              disabled={!canProcess || validating}
            >
              Avvia controllo dati
            </Button>
          </Space>
        </Space>
      </Card>

      {validation && (
        <Card style={{ marginTop: 16 }}>
          <Alert
            type={validation.valid ? 'success' : 'error'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              validation.valid
                ? 'Tutti i file sono validi. Puoi avviare il controllo dei dati.'
                : 'Sono presenti errori di validazione: correggi i file indicati.'
            }
          />
          <Divider orientation="left">Dettaglio validazione</Divider>
          {renderFileResult('A', validation.files.A)}
          {renderFileResult('B', validation.files.B)}
        </Card>
      )}

      {result && renderResult(result)}
    </div>
  );
};

export default ControlloQuantitaOron;
